import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { userPositionApi } from "api/userPosition.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { UserPosition } from "types/user-position";
import { getTitle } from "utils";
import {
  ActionButton,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { PositionModal } from "./components/PositionModal";

import "../Passport/styles/PassportPage.scss";

export const PositionPage = ({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 10,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<UserPosition[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");
  const [selectedPosition, setSelectedPosition] = useState<
    Partial<UserPosition>
  >({});

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery({ ...query, search: keyword, page: 1, limit: 20 }),
      300,
    ),
    [setQuery, query],
  );

  useEffect(() => {
    document.title = getTitle(title);
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await userPositionApi.findAll(query);
    setLoading(false);
    setData(res.data.userPositions);
    setTotal(res.data.total ?? res.data.userPositions?.length ?? 0);
  };

  const handleDelete = async (row: UserPosition) => {
    setLoading(true);
    try {
      await userPositionApi.delete(row.id);
      message.success("Xóa thành công");
      fetchData();
    } finally {
      setLoading(false);
    }
  };

  const columns: TableColumnsType<UserPosition> = [
    {
      title: "STT",
      key: "stt",
      width: 80,
      render: (_, __, index) => <span>{(query.page - 1) * query.limit + index + 1}</span>,
    },
    {
      title: "Tên",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Thao tác",
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div className="flex gap-2 justify-center items-center">
          <Tooltip title="Cập nhật">
            <ActionButton
              aria-label="Cập nhật"
              onClick={() => {
                setSelectedPosition(record);
                setVisibleModal(true);
                setModalStatus("update");
              }}
            >
              <EditOutlined style={{ color: "#6c84e5" }} />
            </ActionButton>
          </Tooltip>
          <Tooltip title="Xóa">
            <Popconfirm
              onConfirm={() => handleDelete(record)}
              placement="top"
              title="Xác nhận xóa"
            >
              <ActionButton aria-label="Xóa">
                <DeleteOutlined style={{ color: "#ef4444" }} />
              </ActionButton>
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm kiếm",
              onChange: (value) => {
                setSearch(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            leftActions={
              <AppButton
                onClick={() => {
                  setVisibleModal(true);
                  setModalStatus("create");
                  setSelectedPosition({});
                }}
                tone="primary"
                icon={<PlusOutlined />}
              >
                Thêm mới
              </AppButton>
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<UserPosition>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            className="passportTb"
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              query.page = page;
              query.limit = limit;
              setQuery({ ...query });
            }}
          />
        }
      />

      <PositionModal
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        userPosition={selectedPosition}
        status={modalStatus}
      />
    </div>
  );
};
